import React, { createContext, useContext, useEffect, useState } from "react";
import { pusherClient } from "@/lib/pusher/pusher-client";
import { PusherConnectionStatus } from "@/lib/pusher/types";
import { ActionCableConnectionStatus } from "@/lib/actioncable/types";
import { env } from "@/env";

interface RealtimeContextValue {
  connectionStatus: PusherConnectionStatus;
  isPusherEnabled: boolean;
  isActionCableEnabled: boolean;
  actionCableUrl: string | null;
  actionCableConnectionStatus: ActionCableConnectionStatus;
  setActionCableConnectionStatus: (status: ActionCableConnectionStatus) => void;
}

const RealtimeContext = createContext<RealtimeContextValue>({
  connectionStatus: {
    state: "disconnected",
    isConnected: false,
  },
  isPusherEnabled: false,
  isActionCableEnabled: false,
  actionCableUrl: null,
  actionCableConnectionStatus: {
    state: "disconnected",
    isConnected: false,
  },
  setActionCableConnectionStatus: () => {},
});

export const useRealtime = () => useContext(RealtimeContext);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const [connectionStatus, setConnectionStatus] =
    useState<PusherConnectionStatus>({
      state: "disconnected",
      isConnected: false,
    });

  const [actionCableConnectionStatus, setActionCableConnectionStatus] =
    useState<ActionCableConnectionStatus>({
      state: "disconnected",
      isConnected: false,
    });

  const isPusherEnabled =
    typeof window !== "undefined" && !!env.NEXT_PUBLIC_PUSHER_KEY;

  const isActionCableEnabled = typeof window !== "undefined";
  const actionCableUrl = env.NEXT_PUBLIC_ACTIONCABLE_URL ?? null;

  useEffect(() => {
    if (isPusherEnabled) {
      Promise.resolve().then(() => {
        try {
          pusherClient.initialize({
            key: env.NEXT_PUBLIC_PUSHER_KEY!,
            cluster: env.NEXT_PUBLIC_PUSHER_CLUSTER,
            authEndpoint: `${env.NEXT_PUBLIC_FLUID_API_URL}/public/stable/pusher/auth`,
          });

          setConnectionStatus({
            state: "connected",
            isConnected: true,
          });

          console.log("[RealtimeProvider] Pusher initialized");
        } catch (error) {
          console.error(
            "[RealtimeProvider] Pusher initialization error:",
            error
          );
          setConnectionStatus({
            state: "disconnected",
            isConnected: false,
            error: error as Error,
          });
        }
      });
    } else {
      console.log("[RealtimeProvider] Pusher disabled");
      Promise.resolve().then(() => {
        setConnectionStatus({
          state: "unavailable",
          isConnected: false,
        });
      });
    }

    let intervalId: NodeJS.Timeout | null = null;
    if (isPusherEnabled) {
      intervalId = setInterval(() => {
        const state = pusherClient.getConnectionState();
        const isConnected = pusherClient.isConnected();

        setConnectionStatus((prev) => {
          if (prev.state !== state || prev.isConnected !== isConnected) {
            return {
              state: state as PusherConnectionStatus["state"],
              isConnected,
            };
          }
          return prev;
        });
      }, 5000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (isPusherEnabled) {
        pusherClient.disconnect();
      }
    };
  }, [isPusherEnabled]);

  return (
    <RealtimeContext.Provider
      value={{
        connectionStatus,
        isPusherEnabled,
        isActionCableEnabled,
        actionCableUrl,
        actionCableConnectionStatus,
        setActionCableConnectionStatus,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
}
